import { useNode } from '@craftjs/core';
import { Box, BoxProps, FormControl, FormLabel, Stack, RadioGroup, FormControlLabel, Radio, TextField} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useState, useEffect } from 'react';

interface CInputProps {
  text: any;
  size?: any;
  fontSize?: any;
  fontWeight?: any;
  textAlign?: any;
  width?: any,
  height?: any,
  paddingV?: any,
  paddingH?: any,
  color?: any;
  borderRadius?: any;
  borderColor?: any;
  borderWidth?: any;
  [key: string]: any;
}

export const CInput = ({ 
  text, 
  fontSize, 
  fontWeight, 
  textAlign, 
  paddingV,
  paddingH, 
  color, 
  width,
  height,
  borderRadius,
  borderColor,
  borderWidth,
  ...props 
}: CInputProps) => {
  const [sText, setSText] = useState(text);
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  useEffect(() => {
    if (selected) {
      return;
    }
  }, [selected]);

  const pmstr = (value: any) => {
    return (!isNaN(value) && !isNaN(parseFloat(value))) ? `${value}px` : value;
  }

  return (
    <Box
      {...props}
      className="craft-input"
      ref={(ref: any) => connect(drag(ref))}
      style={{
        borderColor: selected ? '#008522' : '#F0F0F0',
        margin: '0px !important',
        width: '100%',
      }}
    >
      <TextField
        value={sText}
        onChange={(e) => setSText(e.target.value)}
        sx={{ 
          fontSize: `${fontSize}px`, 
          textAlign: textAlign, 
          fontWeight: fontWeight,
          width: `${width} !important`,
          height: `${height} !important`,
          color: color,
          '& >.MuiInputBase-root': {
            width: `${width} !important`,
            height: `${height} !important`,
            borderColor: selected ? '#008522' : borderColor,
            borderRadius: borderRadius,
            borderWidth: borderWidth,
            borderStyle: "solid",
          },
          '& >.MuiInputBase-root > input': {
            width: `${width} !important`,
            height: `${height} !important`,
            padding: `${pmstr(paddingV)} ${pmstr(paddingH)} ${pmstr(paddingV)} ${pmstr(paddingH)}`, 
          },
          '& >.MuiInputBase-root > fieldset': {
            border: 'none',
          }
        }}
      />
    </Box>
  );
};

const CInputSettings = () => {
  const {
    actions: { setProp },
    text,
    fontSize,
    color,
    textAlign,
    fontWeight,
    width,
    height,
    paddingV,
    paddingH,
    borderRadius,
    borderColor,
    borderWidth,
  } = useNode((node) => ({
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    color: node.data.props.color,
    textAlign: node.data.props.textAlign,
    fontWeight: node.data.props.fontWeight,
    width: node.data.props.width,
    height: node.data.props.height,
    paddingV: node.data.props.paddingV,
    paddingH: node.data.props.paddingH,
    borderRadius: node.data.props.borderRadius,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
  }));

  return (
    <Stack spacing={2} paddingBottom={4}>
      <FormControl size="small" component="fieldset" sx={{
        '* > .MuiInputBase-root': {
          height: 'auto !important',
        }
      }}>
        <FormLabel component="legend">Text</FormLabel>
        <TextField
          multiline
          rows={4}
          value={text}
          onChange={(e) =>
            setProp((props: any) => (props.text = e.target.value))
          }
          style={{
            height: 'auto',
          }}
          variant="outlined"
          size="small"
        />
      </FormControl>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Font size</FormLabel>
        <TextField
          value={fontSize}
          onChange={(e) =>
            setProp((props: any) => (props.fontSize = e.target.value))
          }
          variant="outlined"
          size="small"
        />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Font Weight</FormLabel>
        <RadioGroup
          defaultValue={fontWeight}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.fontWeight = e.target.value))
          }
        >
          <FormControlLabel
            label="100"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="200"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="300"
            value="300"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="400"
            value="400"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="500"
            value="500"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="600"
            value="600"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="700"
            value="700"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="800"
            value="800"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="900"
            value="900"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Text Align</FormLabel>
        <RadioGroup
          defaultValue={textAlign}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.textAlign = e.target.value))
          }
        >
          <FormControlLabel
            label="Left"
            value="left"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Center"
            value="center"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Right"
            value="right"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Width</FormLabel>
          <TextField
            value={width}
            onChange={(e) =>
              setProp((props: any) => (props.width = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Height</FormLabel>
          <TextField
            value={height}
            onChange={(e) =>
              setProp((props: any) => (props.height = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Vertical</FormLabel>
          <TextField
            value={paddingV}
            onChange={(e) =>
              setProp((props: any) => (props.paddingV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Horizontal</FormLabel>
          <TextField
            value={paddingH}
            onChange={(e) =>
              setProp((props: any) => (props.paddingH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Color</FormLabel>
        <MuiColorInput format='hex' value={color} onChange={(color) => {
            setProp((props: any) => (props.color = color), 500);
          }}
        />
      </FormControl>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Width</FormLabel>
          <TextField
            value={borderWidth}
            onChange={(e) =>
              setProp((props: any) => (props.borderWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
            setProp((props: any) => (props.borderColor = color), 500);
          }} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Radius</FormLabel>
          <TextField
            value={borderRadius}
            onChange={(e) =>
              setProp((props: any) => (props.borderRadius = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const CInputDefaultProps = {
  text: 'Hi',
  fontSize: 16,
  color: '#000000',
  textAlign: 'left',
  variant: 'p',
  fontWeight: 300,
  paddingV: '8px',
  paddingH: '10px',
  width: '100%',
  height: 'auto',
  borderRadius: '4px',
  borderWidth: '2px',
  borderColor: '#00571f',
};

CInput.craft = {
  displayName: "Input",
  props: CInputDefaultProps,
  related: {
    settings: CInputSettings,
  },
};
